import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { formatCivilDate, formatInstant } from '@/shared/format/date';
import { formatCents, type Cents } from '@/shared/format/money';

// Formato de entrada: o `AmortizationReadModel` e o `PaymentScheduleItemReadModel` do legado
// (services/creditNote/types/creditNoteReadModel.ts:112-123 e :287-347), que são a resposta de
// `POST /Amortization`, `GET /Amortization/{id}` e o par `amortization` + `paymentScheduleItems` da
// operação (OperationTabContainer.tsx:17-19). Dinheiro em centavos inteiros; taxa em fração.

const cents = z.number() as z.ZodType<Cents>;
const nullishCents = cents.nullish();
const nullishText = z.string().nullish();

/** Uma parcela do plano de pagamento (`PaymentScheduleItemReadModel`, creditNoteReadModel.ts:112-123). */
export const paymentScheduleItemSchema = z.looseObject({
  id: nullishText,
  /** Sem número, a tabela mostra o índice a partir de 0, como o legado (OperationDetails.tsx:289). */
  paymentNo: z.number().nullish(),
  dueDate: z.string(),
  /** "Saldo Devedor". */
  principalAmountInCents: cents,
  amortization: cents,
  interest: cents,
  /** "IOF", só no plano customizado. */
  financeTax: nullishCents,
  payment: cents,
  accruedInterest: nullishCents,
  /** "Tarifas", com o regime de previdência incluído. */
  addedFixedCosts: nullishCents,
});
export type PaymentScheduleItem = z.infer<typeof paymentScheduleItemSchema>;

/** O resultado de uma simulação (`AmortizationReadModel`, creditNoteReadModel.ts:287-347), só o que a tela lê. */
export const amortizationResultSchema = z.looseObject({
  /** Id da simulação (`POST /Amortization`): serve para recuperar. */
  id: nullishText,
  amortizationType: nullishText,
  startDate: nullishText,
  dueDate: nullishText,
  numberOfPayments: z.number().nullish(),
  paymentPeriodicity: z.looseObject({ every: z.number().nullish(), periodicity: nullishText }).nullish(),
  periodicity: nullishText,
  indexer: nullishText,
  /** Fração do indexador pós-fixado (1 = 100% do CDI). */
  indexerValue: z.number().nullish(),
  initialValue: nullishCents,
  comm: nullishCents,
  commTc: nullishCents,
  commNonTc: nullishCents,
  finTax: nullishCents,
  liquidValue: nullishCents,
  paymentFixedCosts: nullishCents,
  monthlyInterest: z.number().nullish(),
  yearlyInterest: z.number().nullish(),
  effectiveMonthlyCost: z.number().nullish(),
  effectiveYearlyCost: z.number().nullish(),
  paymentScheduleItems: z.array(paymentScheduleItemSchema).nullish(),
});
export type AmortizationResult = z.infer<typeof amortizationResultSchema>;

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * `moment(date).format('DD/MM/YYYY')` (OperationDetails.tsx:306, AmortizationQuery.tsx:254): com
 * offset, o dia do instante em São Paulo; sem offset, a data como foi escrita. Vazio = `null` (o
 * legado mostrava hoje, `moment(undefined)`).
 */
export function amortizationDate(value: unknown, locale = 'pt-BR'): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const text = value.trim();
  if (text.includes('T') && HAS_OFFSET.test(text)) return formatInstant(text, { locale, time: false });
  return formatCivilDate(text, { locale }) ?? formatInstant(text, { locale, time: false });
}

/** `formatCurrencyInCents` (helpers/formats/Currency.ts:32-46): centavos → BRL; vazio vira R$ 0,00. */
export function amortizationMoney(value: unknown, locale = 'pt-BR'): string {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof number !== 'number' || !Number.isFinite(number)) return formatCents(0, { locale });
  return formatCents(Math.round(number), { locale });
}

function decimalSeparator(locale: string): string {
  return new Intl.NumberFormat(locale).formatToParts(1.5).find((part) => part.type === 'decimal')?.value ?? ',';
}

/**
 * `formatPercentage(v, 4)` (Currency.ts:73-82): fração × 100 com `toFixed` (arredonda); vazio ou 0 →
 * `0%`. O separador é o do idioma (vírgula em pt-BR e es, como o legado).
 */
export function amortizationPercent(value: unknown, locale = 'pt-BR', places = 4): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return '0%';
  return `${(value * 100).toFixed(places).replace('.', decimalSeparator(locale))}%`;
}

/**
 * `formatPercentageWithPlaces(v, 4)` (Currency.ts:111-117): fração × 100 **truncada**, deslocando a
 * vírgula no texto para não perder a última casa no ponto flutuante. Taxa A.M e os dois CET.
 */
export function amortizationTruncatedPercent(value: unknown, locale = 'pt-BR', places = 4): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return '0%';
  const [mantissa = '0', exponent = '0'] = Math.abs(numeric).toString().split(/e/i);
  const [integerDigits = '0', fractionDigits = ''] = mantissa.split('.');
  const shifted = integerDigits.length + 2 + Number(exponent);
  const leftPad = Math.max(0, 1 - shifted);
  const point = shifted + leftPad;
  const digits = `${'0'.repeat(leftPad)}${integerDigits}${fractionDigits}`.padEnd(point + places, '0');
  const integerPart = digits.slice(0, point).replace(/^0+(?=\d)/, '');
  const fractionPart = digits.slice(point, point + places);
  const sign = numeric < 0 ? '-' : '';
  return `${sign}${integerPart}${places > 0 ? `${decimalSeparator(locale)}${fractionPart}` : ''}%`;
}

/**
 * O indexador como o legado escreve (`getValuesIndexer`, AmortizationQuery.tsx:104-119):
 * `100% CDI`; sem indexador, `null` (quem chama mostra "Sem indexador pós-fixado").
 */
export function indexerText(result: Pick<AmortizationResult, 'indexer' | 'indexerValue'> | null | undefined): string | null {
  const value = result?.indexerValue;
  if (!value) return null;
  return `${String(value * 100)}% ${result.indexer ?? ''}`.trim();
}

/** Soma das parcelas: o "Total" da tabela e o "Valor futuro" dos resultados (OperationDetails.tsx:172). */
export function scheduleTotal(items: readonly Pick<PaymentScheduleItem, 'payment'>[] | null | undefined): Cents {
  return (items ?? []).reduce((sum, item) => sum + (Number.isFinite(item.payment) ? item.payment : 0), 0);
}

/** Nenhum valor verdadeiro no resultado: o legado mostra o aviso de "Simular" (AmortizationQuery.tsx:147). */
export function isEmptyResult(result: AmortizationResult | null | undefined): boolean {
  if (!result) return true;
  return !Object.values(result).some((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)));
}

// ─── prazo (helpers/formats/DateFormat.ts:24-137) ────────────────────────────

export interface TermParts {
  readonly years: number;
  readonly months: number;
  readonly days: number;
}

const BRASILIA_OFFSET_MS = 3 * 60 * 60 * 1000;

function calendarParts(value: string | number): { year: number; month: number; day: number } | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const brasilia = new Date(date.getTime() - BRASILIA_OFFSET_MS);
  return { year: brasilia.getUTCFullYear(), month: brasilia.getUTCMonth(), day: brasilia.getUTCDate() };
}

/** `dateDiff` (DateFormat.ts:24-97): anos, meses e dias no calendário de Brasília; fim ausente = `now`. */
export function termBetween(start: unknown, end: unknown, now: number): TermParts | null {
  if (typeof start !== 'string' || start === '') return null;
  let from = calendarParts(start);
  let to = calendarParts(typeof end === 'string' && end !== '' ? end : now);
  if (!from || !to) return null;
  const after = from.year > to.year || (from.year === to.year && (from.month > to.month || (from.month === to.month && from.day > to.day)));
  if (after) [from, to] = [to, from];
  const february = (from.year % 4 === 0 && from.year % 100 !== 0) || from.year % 400 === 0 ? 29 : 28;
  const daysInMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let years = to.year - from.year;
  let months = to.month - from.month;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  let days = to.day - from.day;
  if (days < 0) {
    if (months > 0) months -= 1;
    else {
      years -= 1;
      months = 11;
    }
    days += daysInMonth[from.month] ?? 30;
  }
  return { years, months, days };
}

/**
 * `resolveOperationTerm` (DateFormat.ts:108-122): mensal com parcelas → parcelas em anos e meses; senão a
 * diferença entre início e vencimento. `null` = "N/D".
 */
export function operationTerm(result: AmortizationResult | null | undefined, now: number): TermParts | null {
  const periodicity = result?.paymentPeriodicity?.periodicity || result?.periodicity;
  const payments = result?.numberOfPayments;
  if (periodicity?.toLowerCase() === 'monthly' && payments) return { years: Math.floor(payments / 12), months: payments % 12, days: 0 };
  return termBetween(result?.startDate, result?.dueDate, now);
}

/** O prazo como o legado escreve ("2 anos e 3 meses", "18 meses", "12 dias"). */
export function termText(i18n: I18n, parts: TermParts | null): string {
  if (!parts) return i18n._(msg`N/D`);
  const { years, months, days } = parts;
  const yearText = years === 1 ? i18n._(msg`${years} ano`) : i18n._(msg`${years} anos`);
  const monthText = months === 1 ? i18n._(msg`${months} mês`) : i18n._(msg`${months} meses`);
  if (years > 0 && months > 0) return i18n._(msg`${yearText} e ${monthText}`);
  if (years > 0) return yearText;
  if (months > 0) return monthText;
  return days === 1 ? i18n._(msg`${days} dia`) : i18n._(msg`${days} dias`);
}
